import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  Tabs,
  Tab,
  Grid,
  TableContainer,
  CircularProgress
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { getAppointmentsByPatientId } from "../../services/appointmentService";
import { getPatientById } from "../../services/patientService";
import { getVitalSignsByPatientId, filterVitalSignsPreferPrescription } from "../../services/vitalSignsService";
import { getPrescriptionsByPatientId, updatePrescription } from "../../services/prescriptionService";
import { getAllMedicine, createNewMedicine } from "../../services/medicineNewService";
import { getPathLabReportsForPatient, hasSubCategories } from "../PathManagePatient/labTestWorkflowUtils";
import { getLabtestsList } from "../../services/labtestService";
import { getAllDoctors } from "../../services/doctorService";
import { filterByOrganization } from "../../utils/organizationUtils";
import IpdSummary from "./IpdSummary";
import Header from "../../components/Header";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import { getOrganizationName } from "../../utils/organizationUtils";
import {
  buildOpdSummaryDocumentHtml,
  buildPatientDocumentStyles,
  exportPatientSummaryDocumentToPdf,
  getPatientPdfFilename,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";
import { toPeriodDateKey } from "../../utils/patientPeriodSummary";
import {
  displayId,
  formatTimelineDateTime,
  getAppointmentDateTimeValue,
  getAppointmentId,
  getLinkedAppointmentId,
  getLinkedPrescriptionId,
  getPrescriptionRecordId,
  idsEqual,
  parseTimelineDateTime,
  toVisitScopeId,
  recordBelongsToVisitWindow,
  labRecordKey,
  labIdentityKeys,
  dedupeLabRecords,
  resolveTimelineFocusTab,
  getDefaultOpdTimelineTabIndex,
} from "../../utils/patientTimelineVisitUtils";
import { useAuth } from "../../context/AuthContext";
import { getAppointmentStatusUpper } from "../../utils/appointmentCancelUtils";
import {
  formatLabReportReferenceDisplay,
  formatReportDateOnly,
  getResultDisplayRows,
} from "../path-lab/labReportDisplayUtils";
import { toast } from "react-toastify";
import PatientTimelinePrintMenu from "../../components/PatientTimelinePrintMenu";
import IssueMedicineModal from "../inventory/Pharmacy/IssueMedicineModal";

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const tabLabels = [
  'Prescriptions',
  'Vital Signs',
  'Lab Test Result',
  'OPD Summary'
];

const dosageOptions = ["1-0-0", "1-0-1", "1-1-0", "1-1-1", "0-0-1", "0-1-0", "0-1-1", "0-0-0"];
const frequencyOptions = ["daily", "weekly", "monthly"];

const PatientTimeline = (props) => {
  const { patientId: routePatientId } = useParams();
  const patientId = props?.patientId ?? routePatientId;
  const onBack = props?.onBack;
  // The row the user clicked (PatientsTable) already fetched this patient's
  // details via getAllPatients() — passed through so we don't immediately
  // re-fetch GET /patients/{id} for data we already have.
  const initialPatientInfo = props?.initialPatientInfo ?? null;
  // Embedding contexts that already show the patient's name/back action
  // elsewhere (e.g. the prescription modal's own title bar) can hide this
  // component's own name + back-arrow heading.
  const showHeader = props?.showHeader ?? true;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const timelineFocusTab = resolveTimelineFocusTab({
    propTab: props?.timelineTab,
    locationState: location?.state,
    searchParams,
  });
  const theme = useTheme();
  const reportRef = useRef();
  // Guards against a slow fetch for a previous patient (or previous visit to
  // the same patient) landing after the user has already navigated on, and
  // clobbering the screen with stale/wrong-patient data.
  const patientFetchGuardRef = useRef({ requestId: 0, unmounted: false });
  useEffect(() => {
    const guard = patientFetchGuardRef.current;
    return () => {
      guard.unmounted = true;
    };
  }, []);
  const colors = tokens(theme.palette.mode);
  const primaryColor = theme.palette.primary.main || "#1976d2";
  // True until the initial per-visit fetch (appointments/patient/vitals/
  // prescriptions) has settled, so tabs can show a spinner instead of a
  // misleading "No data available" while a genuinely-slow backend call is
  // still in flight.
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [activeTabs, setActiveTabs] = useState({});
  const [appointmentList, setAppointmentList] = useState([]);
  const [patientInfo, setPatientInfo] = useState(() => initialPatientInfo);
  const [PrescritionList, setPrescriptionList] = useState([]);
  const [ComplaintList, setComplaintList] = useState([]);
  const [LabtestList, setlabTestList] = useState([]);
  const [vitalSigns, setVitalSigns] = useState([]);
  const [hospitalName, setHospitalName] = useState("ARYAN HOSPITAL");
  const [hospitalAddress, setHospitalAddress] = useState("123 Medical Street, Health City");
  const [hospitalPhone, setHospitalPhone] = useState("");
  const [editingPrescriptionId, setEditingPrescriptionId] = useState(null);
  const [editedMedicines, setEditedMedicines] = useState([]);
  const [savingPrescriptionId, setSavingPrescriptionId] = useState(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [pendingSavePrescription, setPendingSavePrescription] = useState(null);
  const [medicineList, setMedicineList] = useState([]);
  const { user } = useAuth();
  const normalizedRole = String(user?.roleName || user?.role || "").toLowerCase();
  const canEditPrescription = normalizedRole === "doctor";
  const isPharmacyAdmin = normalizedRole === "pharmacy admin";
  const defaultTimelineTabIndex = getDefaultOpdTimelineTabIndex(timelineFocusTab, {
    isPharmacyAdmin,
  });
  const [issueMedicineOpen, setIssueMedicineOpen] = useState(false);
  const [issueMedicinePrescription, setIssueMedicinePrescription] = useState(null);

  useEffect(() => {
    if (patientId == null || patientId === "" || patientId === "undefined") {
      return;
    }
    const requestId = ++patientFetchGuardRef.current.requestId;
    setInitialDataLoading(true);
    const appointmentsPromise = fetchAppointments(requestId);

    // Only trust initialPatientInfo when it's actually for this patientId —
    // guards against a stale prop from a previous navigation still lingering
    // on the same mounted instance (e.g. deep-linking between patients).
    const seedId =
      initialPatientInfo?.id ?? initialPatientInfo?.patientId ?? initialPatientInfo?.patient_id;
    let patientInfoPromise;
    if (initialPatientInfo != null && seedId != null && String(seedId) === String(patientId)) {
      setPatientInfo(initialPatientInfo);
      patientInfoPromise = Promise.resolve();
    } else {
      patientInfoPromise = fetchPatientInfo(requestId);
    }

    const vitalsPromise = fetchVitalSigns(requestId);
    fetchHospitalInfo();

    // Fetch prescriptions once and share the result with the Prescriptions tab
    // and the lab-test merge (getPathLabReportsForPatient), which otherwise each
    // independently call GET /prescriptions/patient/{id}.
    const prescriptionsPromise = getPrescriptionsByPatientId(patientId)
      .catch((error) => {
        console.error(`[OpdReports] Error fetching prescriptions:`, error);
        return [];
      })
      .then((prescriptions) => {
        const list = Array.isArray(prescriptions) ? prescriptions : [];
        return Promise.all([
          fetchPatientPrescription(list, requestId),
          fetchTestByPatient(list, requestId),
        ]);
      });

    Promise.allSettled([
      appointmentsPromise,
      patientInfoPromise,
      vitalsPromise,
      prescriptionsPromise,
    ]).then(() => {
      if (patientFetchGuardRef.current.requestId === requestId && !patientFetchGuardRef.current.unmounted) {
        setInitialDataLoading(false);
      }
    });
    // location.key changes on every navigation (even revisiting the same patient),
    // so this refetches on each visit instead of reusing a stale snapshot from
    // before a prescription/lab test was just created elsewhere — same pattern
    // already used in PatientInQueue.jsx for the same reason.
  }, [patientId, location.key, initialPatientInfo]);

  useEffect(() => {
    // medicineList is only used by the edit-prescription flow (findMedicineIdByName,
    // getDistinctMedicines in the edit UI), which is doctor-only.
    if (!canEditPrescription) return;
    const fetchMedicineList = async () => {
      try {
        const data = await getAllMedicine();
        const transformedData = Array.isArray(data)
          ? data.map((medicine) => ({
              id: medicine.id,
              medicineId: medicine.id,
              name: medicine.medicineName || medicine.name || "",
              medicineName: medicine.medicineName || medicine.name || "",
            }))
          : [];
        setMedicineList(transformedData);
      } catch (error) {
        console.error("[OpdReports] Error fetching medicines:", error);
        setMedicineList([]);
      }
    };
    fetchMedicineList();
  }, [canEditPrescription]);

  // Fetch hospital/organization information for PDF header
  const fetchHospitalInfo = async () => {
    try {
      const storedUser = localStorage.getItem("user");
      let organizationId = null;
      let organizationName = null;
      let organizationAddress = null;

      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          organizationId = user?.organizationId || localStorage.getItem("organizationId");
          organizationName = user?.organizationName;
          
          if (organizationId && typeof organizationId === 'string') {
            organizationId = parseInt(organizationId, 10);
          }
        } catch (e) {
          console.error("[OpdReports] Error parsing stored user:", e);
        }
      }

      // Try to get organization name from utility function
      if (!organizationName) {
        organizationName = getOrganizationName(null); // Pass null to use localStorage
      }

      // Always fetch full organization data if we have organizationId (to get address)
      if (organizationId && organizationId > 0) {
        try {
          // Try hospital organizations first
          const { getHospitalOrganizationById } = await import("../../services/hospitalOrganizationService");
          try {
            const hospitalOrg = await getHospitalOrganizationById(organizationId);
            console.log(`[OpdReports] Full hospital organization response:`, hospitalOrg);
            
            if (hospitalOrg) {
              // Get organization name if not already set
              if (!organizationName) {
                organizationName = hospitalOrg.name || null;
              }
              
              // Try to get address from various possible fields
              // Check single address field first
              if (hospitalOrg.address) {
                organizationAddress = hospitalOrg.address;
              } else if (hospitalOrg.location) {
                organizationAddress = hospitalOrg.location;
              } else if (hospitalOrg.fullAddress) {
                organizationAddress = hospitalOrg.fullAddress;
              } else {
                // Construct address from separate fields (address, city, state, pincode)
                const addressParts = [];
                if (hospitalOrg.address) addressParts.push(hospitalOrg.address);
                if (hospitalOrg.streetAddress) addressParts.push(hospitalOrg.streetAddress);
                if (hospitalOrg.city) addressParts.push(hospitalOrg.city);
                if (hospitalOrg.state) addressParts.push(hospitalOrg.state);
                if (hospitalOrg.pincode) addressParts.push(hospitalOrg.pincode);
                if (hospitalOrg.zipCode) addressParts.push(hospitalOrg.zipCode);
                
                if (addressParts.length > 0) {
                  organizationAddress = addressParts.join(", ");
                }
              }
              
              console.log(`[OpdReports] ✅ Fetched hospital organization:`, { 
                name: organizationName, 
                address: organizationAddress,
                rawFields: {
                  address: hospitalOrg.address,
                  location: hospitalOrg.location,
                  city: hospitalOrg.city,
                  state: hospitalOrg.state,
                  pincode: hospitalOrg.pincode
                }
              });
              const orgPhone =
                hospitalOrg.phone ||
                hospitalOrg.mobile ||
                hospitalOrg.contactNumber ||
                hospitalOrg.phoneNumber;
              if (orgPhone) setHospitalPhone(String(orgPhone));
            }
          } catch (hospitalError) {
            console.log(`[OpdReports] Hospital organization not found, trying regular organizations...`);
            // If hospital organization not found, try regular organizations
            try {
              const { getOrganizationById } = await import("../../services/organizationService");
              const org = await getOrganizationById(organizationId);
              console.log(`[OpdReports] Full regular organization response:`, org);
              
              if (org) {
                // Get organization name if not already set
                if (!organizationName) {
                  organizationName = org.name || null;
                }
                
                // Try to get address from various possible fields
                if (org.address) {
                  organizationAddress = org.address;
                } else if (org.location) {
                  organizationAddress = org.location;
                } else if (org.fullAddress) {
                  organizationAddress = org.fullAddress;
                } else {
                  // Construct address from separate fields
                  const addressParts = [];
                  if (org.address) addressParts.push(org.address);
                  if (org.streetAddress) addressParts.push(org.streetAddress);
                  if (org.city) addressParts.push(org.city);
                  if (org.state) addressParts.push(org.state);
                  if (org.pincode) addressParts.push(org.pincode);
                  if (org.zipCode) addressParts.push(org.zipCode);
                  
                  if (addressParts.length > 0) {
                    organizationAddress = addressParts.join(", ");
                  }
                }
                
                console.log(`[OpdReports] ✅ Fetched regular organization:`, { 
                  name: organizationName, 
                  address: organizationAddress,
                  rawFields: {
                    address: org.address,
                    location: org.location,
                    city: org.city,
                    state: org.state,
                    pincode: org.pincode
                  }
                });
                const orgPhone =
                  org.phone ||
                  org.mobile ||
                  org.contactNumber ||
                  org.phoneNumber;
                if (orgPhone) setHospitalPhone(String(orgPhone));
              }
            } catch (orgError) {
              console.warn(`[OpdReports] Organization ${organizationId} not found in either service:`, orgError);
            }
          }
        } catch (error) {
          console.error(`[OpdReports] Error fetching organization info:`, error);
        }
      }

      // Set hospital name and address (use defaults if not found)
      setHospitalName(organizationName || "ARYAN HOSPITAL");
      setHospitalAddress(organizationAddress || "123 Medical Street, Health City");
    } catch (error) {
      console.error("[OpdReports] Error fetching hospital info:", error);
      // Use defaults on error
      setHospitalName("ARYAN HOSPITAL");
      setHospitalAddress("123 Medical Street, Health City");
    }
  };

  const handleGenerateBill = () => {
    navigate(`/ipd-invoice/${patientId}`);
  };

  // A slower fetch from a previous patient/visit can land after the user has
  // already navigated on (requestId no longer matches, or the component
  // unmounted) — skip applying its result in that case.
  const isStalePatientFetch = (requestId) =>
    requestId != null &&
    (patientFetchGuardRef.current.unmounted || patientFetchGuardRef.current.requestId !== requestId);

  const fetchAppointments = async (requestId) => {
    try {
      const data = await getAppointmentsByPatientId(patientId);
      if (isStalePatientFetch(requestId)) return;
      setAppointmentList(data || []);
    } catch (error) {
      console.error("Failed to fetch assignments", error);
      if (!isStalePatientFetch(requestId)) setAppointmentList([]);
    }
  };

  const fetchTestByPatient = async (prefetchedPrescriptions = null, requestId) => {
    try {
      let data = await getPathLabReportsForPatient(patientId, {
        prescriptions: Array.isArray(prefetchedPrescriptions) ? prefetchedPrescriptions : undefined,
      });

      // Ensure data is an array
      if (!Array.isArray(data)) {
        data = [];
      }

      // Filter by organization to ensure only organization-specific reports are shown
      const storedUser = localStorage.getItem("user");
      let user = null;
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
        } catch (e) {
          console.error("[OpdReports] Error parsing stored user:", e);
        }
      }

      if (user) {
        const originalCount = data.length;
        data = filterByOrganization(data, user);
        console.log(`[OpdReports] Filtered ${originalCount} lab test reports to ${data.length} for organization`);
      }

      // getPathLabReportsForPatient() already resolves testName per report
      // (via normalizePathLabReportRow), so no second catalog fetch is needed here.
      if (isStalePatientFetch(requestId)) return;
      setlabTestList(data);
    } catch (error) {
      console.error("Failed to fetch patient path report", error);
      if (!isStalePatientFetch(requestId)) setlabTestList([]);
    }
  };

  const fetchVitalSigns = async (requestId) => {
    try {
      const data = await getVitalSignsByPatientId(patientId);
      if (isStalePatientFetch(requestId)) return;
      // Front-desk vitals until prescription exists for that day/appointment; then only prescription vitals
      setVitalSigns(filterVitalSignsPreferPrescription(data));
    } catch (error) {
      console.error("Failed to fetch vital signs", error);
      if (!isStalePatientFetch(requestId)) setVitalSigns([]);
    }
  };

  const fetchPatientInfo = async (requestId) => {
    try {
      const data = await getPatientById(patientId);
      if (isStalePatientFetch(requestId)) return;
      setPatientInfo(data);
    } catch (error) {
      console.error("Failed to fetch assignments", error);
    }
  };

  // Local calendar day key (not UTC). Using toISOString() previously split
  // same-day appointments vs prescriptions across two timeline dates (e.g. IST).
  const formatDateKey = (dateArrayOrString) => {
    if (!dateArrayOrString) {
      console.warn("formatDateKey: Received null/undefined/empty date value");
      return null;
    }
    const dateKey = toPeriodDateKey(dateArrayOrString);
    if (!dateKey) {
      console.warn("formatDateKey: Invalid date value:", dateArrayOrString);
    }
    return dateKey;
  };

  // Builds one display line per sub-category (or a single line for a simple
  // test) for the Result / Ref. Value columns — same helper used by the
  // Pathology Data tab's report table, so both read identically.
  const buildResultReferenceLines = (report) => {
    const display = getResultDisplayRows(report.testName, report.result, {
      subCategories: report.subCategories,
      normalRange: report.normalRange,
      referenceValue: report.referenceValue,
      unit: report.unit,
    });

    if (display.type === "table") {
      return {
        resultLines: display.rows.map((row) => `${row.name}: ${row.result || "—"}`),
        referenceLines: display.rows.map(
          (row) => formatLabReportReferenceDisplay(row.referenceValue, row.unit) || "—"
        ),
        remarks: display.remarks,
      };
    }

    return {
      resultLines: [display.simpleResult || "—"],
      referenceLines: [display.referenceDisplay || "—"],
      remarks: display.remarks,
    };
  };

  // Helper function to check if a test has predefined sub-categories
  // Uses shared catalog so newly added predefined tests (e.g. Semen Analysis) work in report/print.

  // const fetchPatientPrescription = async () => {
  //   try {
  //     const data = await getPrescriptionsByPatientId(patientId);
  //     setPrescriptionList(data); // ✅ not InvoiceList
  //     console.log("Prescriptions fetched", data);
  //   } catch (error) {
  //     console.error("Failed to fetch prescriptions", error);
  //   }
  // };

  // Normalizes one complaint/diagnosis entry to a display string — the
  // backend now returns these as plain string arrays already (embedded
  // directly on the prescription), but this keeps rendering safe if an
  // older-shaped object ever slips through.
  const normalizeTextEntry = (entry) => {
    if (typeof entry === "string") return entry;
    if (entry && typeof entry === "object") {
      return (
        entry.complaint ||
        entry.diagnosis ||
        entry.complaintName ||
        entry.diagnosisName ||
        entry.name ||
        entry.value ||
        JSON.stringify(entry)
      );
    }
    return String(entry ?? "");
  };

  const fetchPatientPrescription = async (prefetchedPrescriptions = null, requestId) => {
    try {
      // Complaints, diagnoses, and medicine names are embedded directly in
      // this response now — no more per-prescription follow-up calls (was:
      // a separate complaints fetch, a diagnoses fetch with a primary+
      // fallback endpoint, and a medicine-catalog/fallback-endpoint chain
      // to resolve medicine names when they were missing).
      const prescriptions = Array.isArray(prefetchedPrescriptions)
        ? prefetchedPrescriptions
        : await getPrescriptionsByPatientId(patientId);

      console.log(`[OpdReports] Received ${Array.isArray(prescriptions) ? prescriptions.length : 0} prescriptions`);

      const updatedPrescriptions = (Array.isArray(prescriptions) ? prescriptions : []).map((rx) => ({
        ...rx,
        complaints: (Array.isArray(rx.complaints) ? rx.complaints : []).map(normalizeTextEntry).filter(Boolean),
        diagnoses: (Array.isArray(rx.diagnoses) ? rx.diagnoses : []).map(normalizeTextEntry).filter(Boolean),
        medicines: Array.isArray(rx.medicines) ? rx.medicines : (Array.isArray(rx.medicine) ? rx.medicine : []),
      }));

      if (isStalePatientFetch(requestId)) return;
      setPrescriptionList(updatedPrescriptions);
    } catch (error) {
      console.error(`[OpdReports] ❌ Error in fetchPatientPrescription:`, error);
      // Set empty array on error to prevent UI crashes
      if (!isStalePatientFetch(requestId)) setPrescriptionList([]);
    }
  };

  const initializeGroup = () => ({
    appointments: [],
    patient: [],
    prescriptions: [],
    complaints: [],
    diagnoses: [],
    vitalSigns: [],
    LabtestList: [],
    dateKey: null,
    appointmentId: null,
    prescriptionId: null,
  });

  const appointmentGroupKey = (appointmentId, fallbackIndex) =>
    appointmentId != null && appointmentId !== ""
      ? `appointment-${appointmentId}`
      : `appointment-unknown-${fallbackIndex}`;

  const resolveCalendarDateKey = (rawValue) => {
    let dateKey = formatDateKey(rawValue);
    if (!dateKey && typeof rawValue === "string" && rawValue.length > 0) {
      const dateMatch = rawValue.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) dateKey = dateMatch[1];
    }
    return dateKey || "unknown-date";
  };

  const findGroupByAppointmentId = (grouped, appointmentId) => {
    const id = toVisitScopeId(appointmentId);
    if (!id) return null;
    return (
      grouped[appointmentGroupKey(id)] ||
      Object.values(grouped).find((group) => toVisitScopeId(group.appointmentId) === id) ||
      null
    );
  };

  const findGroupByPrescriptionId = (grouped, prescriptionId) => {
    if (prescriptionId == null || prescriptionId === "") return null;
    const withPrescription = Object.values(grouped).filter((group) =>
      (group.prescriptions || []).some((rx) =>
        idsEqual(getPrescriptionRecordId(rx), prescriptionId)
      )
    );
    if (withPrescription.length === 1) return withPrescription[0];
    if (withPrescription.length > 1) return withPrescription[0];

    const withHeaderId = Object.values(grouped).filter((group) =>
      idsEqual(group.prescriptionId, prescriptionId)
    );
    return withHeaderId.length === 1 ? withHeaderId[0] : null;
  };

  const attachPrescriptionToGroup = (group, rx) => {
    if (!group || !rx) return;
    const prescriptionWithMedicines = {
      ...rx,
      medicines: Array.isArray(rx.medicines)
        ? rx.medicines
        : Array.isArray(rx.medicine)
        ? rx.medicine
        : [],
    };
    group.prescriptions.push(prescriptionWithMedicines);
    if (group.prescriptionId == null) {
      group.prescriptionId = getPrescriptionRecordId(rx);
    }

    if (rx.complaints && Array.isArray(rx.complaints) && rx.complaints.length > 0) {
      const complaintStrings = rx.complaints
        .map((c) => {
          if (typeof c === "string") return c;
          return c.complaint || c.complaintName || c.name || c.value || String(c);
        })
        .filter(Boolean);
      group.complaints.push(...complaintStrings);
    }

    if (rx.diagnoses && Array.isArray(rx.diagnoses) && rx.diagnoses.length > 0) {
      const diagnosisStrings = rx.diagnoses
        .map((d) => {
          if (typeof d === "string") return d;
          return d.diagnosis || d.diagnosisName || d.name || d.value || String(d);
        })
        .filter(Boolean);
      group.diagnoses.push(...diagnosisStrings);
    }
  };

  // Memoized: this does real nested-loop work (cross-referencing appointments,
  // prescriptions, vitals, and lab reports) and was previously re-run on every
  // render regardless of whether the underlying data actually changed.
  const groupedData = useMemo(() => {
    const grouped = {};
    const seenAppointmentIds = new Set();

    appointmentList.forEach((item, index) => {
      const appointmentId = toVisitScopeId(getAppointmentId(item));
      if (appointmentId && seenAppointmentIds.has(appointmentId)) {
        console.warn(`[OpdReports] Skipping duplicate appointment with ID: ${appointmentId}`);
        return;
      }
      if (appointmentId) {
        seenAppointmentIds.add(appointmentId);
      }

      const groupKey = appointmentGroupKey(appointmentId, index);
      if (!grouped[groupKey]) grouped[groupKey] = initializeGroup();
      grouped[groupKey].appointments.push(item);
      grouped[groupKey].appointmentId = appointmentId;
      grouped[groupKey].prescriptionId =
        getLinkedPrescriptionId(item) || grouped[groupKey].prescriptionId;
      grouped[groupKey].dateKey = resolveCalendarDateKey(
        getAppointmentDateTimeValue(item)
      );
    });

    const usedPrescriptionIds = new Set();
    const usedPrescriptionRefs = new Set();

    const markPrescriptionUsed = (rx) => {
      if (rx) usedPrescriptionRefs.add(rx);
      const rxId = getPrescriptionRecordId(rx);
      if (rxId != null) usedPrescriptionIds.add(String(rxId));
    };

    const isPrescriptionUsed = (rx) => {
      if (usedPrescriptionRefs.has(rx)) return true;
      const rxId = getPrescriptionRecordId(rx);
      return rxId != null && usedPrescriptionIds.has(String(rxId));
    };

    PrescritionList.forEach((rx) => {
      const rxAppointmentId = getLinkedAppointmentId(rx);
      const matchedByAppointment = findGroupByAppointmentId(grouped, rxAppointmentId);
      if (matchedByAppointment) {
        attachPrescriptionToGroup(matchedByAppointment, rx);
        markPrescriptionUsed(rx);
      }
    });

    PrescritionList.forEach((rx) => {
      if (isPrescriptionUsed(rx)) return;
      const rxId = getPrescriptionRecordId(rx);
      const matches = Object.values(grouped).filter((group) =>
        idsEqual(group.prescriptionId, rxId)
      );
      if (matches.length === 1) {
        attachPrescriptionToGroup(matches[0], rx);
        markPrescriptionUsed(rx);
      }
    });

    PrescritionList.forEach((rx) => {
      if (isPrescriptionUsed(rx)) return;
      const rxId = getPrescriptionRecordId(rx);
      const dateKey = resolveCalendarDateKey(rx.createdAt || rx.created_at);
      const sameDayGroups = Object.values(grouped).filter(
        (group) => group.dateKey === dateKey && (group.appointments || []).length > 0
      );
      const sameDayVisits = sameDayGroups.map((group) => ({
        id: toVisitScopeId(group.appointmentId),
        dateTime: getAppointmentDateTimeValue(group.appointments?.[0]),
      }));

      if (sameDayGroups.length === 1) {
        attachPrescriptionToGroup(sameDayGroups[0], rx);
        markPrescriptionUsed(rx);
        return;
      }

      if (sameDayGroups.length > 1) {
        const rxTime = rx.createdAt || rx.created_at || rx.prescriptionDate;
        const matchedByTime = sameDayGroups.find((group) =>
          recordBelongsToVisitWindow(rxTime, group.appointmentId, sameDayVisits)
        );
        if (matchedByTime) {
          attachPrescriptionToGroup(matchedByTime, rx);
          markPrescriptionUsed(rx);
          return;
        }
      }

      const orphanKey = rxId != null ? `prescription-${rxId}` : `prescription-unknown-${dateKey}`;
      if (!grouped[orphanKey]) grouped[orphanKey] = initializeGroup();
      grouped[orphanKey].dateKey = dateKey;
      grouped[orphanKey].prescriptionId = rxId;
      attachPrescriptionToGroup(grouped[orphanKey], rx);
      markPrescriptionUsed(rx);
    });

    const findClosestSameDayGroup = (grouped, dateKey, recordTime) => {
      const sameDayGroups = Object.values(grouped).filter(
        (group) => group.dateKey === dateKey
      );
      if (sameDayGroups.length === 0) return null;
      if (sameDayGroups.length === 1) return sameDayGroups[0];
      const recordMs = parseTimelineDateTime(recordTime)?.valueOf();
      if (recordMs == null) return null;
      let closest = null;
      let closestDiff = Infinity;
      sameDayGroups.forEach((group) => {
        const visitMs = parseTimelineDateTime(
          getAppointmentDateTimeValue(group.appointments?.[0])
        )?.valueOf();
        if (visitMs == null) return;
        const diff = Math.abs(visitMs - recordMs);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = group;
        }
      });
      return closest;
    };

    const attachedLabKeys = new Set();
    const labAlreadyAttached = (record) => {
      const keys = labIdentityKeys(record);
      if (keys.length === 0) keys.push(labRecordKey(record));
      return keys.some((key) => attachedLabKeys.has(key));
    };
    const markLabAttached = (record) => {
      const keys = labIdentityKeys(record);
      if (keys.length === 0) keys.push(labRecordKey(record));
      keys.filter(Boolean).forEach((key) => attachedLabKeys.add(key));
    };

    const attachRelatedRecord = (record, section, dateCandidates) => {
      const linkedAppointmentId = getLinkedAppointmentId(record);
      const byAppointment = findGroupByAppointmentId(grouped, linkedAppointmentId);
      if (byAppointment) {
        if (section === "LabtestList") {
          if (labAlreadyAttached(record)) return;
          byAppointment.LabtestList.push(record);
          markLabAttached(record);
        } else {
          byAppointment[section].push(record);
        }
        return;
      }

      const linkedPrescriptionId = getLinkedPrescriptionId(record);
      const byPrescription = findGroupByPrescriptionId(grouped, linkedPrescriptionId);
      if (byPrescription) {
        if (section === "LabtestList") {
          if (labAlreadyAttached(record)) return;
          byPrescription.LabtestList.push(record);
          markLabAttached(record);
        } else {
          byPrescription[section].push(record);
        }
        return;
      }

      let dateKey = null;
      for (const candidate of dateCandidates) {
        dateKey = formatDateKey(candidate);
        if (dateKey) break;
      }
      dateKey = dateKey || "unknown-date";
      if (section === "LabtestList") {
        if (labAlreadyAttached(record)) return;
        const sameDayGroups = Object.values(grouped).filter(
          (group) => group.dateKey === dateKey
        );
        if (sameDayGroups.length === 0) return;
        let target = sameDayGroups[0];
        if (sameDayGroups.length > 1) {
          const sameDayVisits = sameDayGroups.map((group) => ({
            id: toVisitScopeId(group.appointmentId),
            dateTime: getAppointmentDateTimeValue(group.appointments?.[0]),
          }));
          target =
            sameDayGroups.find((group) =>
              recordBelongsToVisitWindow(
                dateCandidates.find(Boolean),
                group.appointmentId,
                sameDayVisits
              )
            ) || null;
        }
        if (!target) return;
        target.LabtestList.push(record);
        markLabAttached(record);
        return;
      }
      const closest = findClosestSameDayGroup(
        grouped,
        dateKey,
        dateCandidates.find(Boolean)
      );
      if (!closest) return;
      closest[section].push(record);
    };

    vitalSigns.forEach((vs) => {
      attachRelatedRecord(vs, "vitalSigns", [
        vs.recordedAt,
        vs.createdAt,
        vs.created_at,
        vs.updatedAt,
      ]);
    });

    LabtestList.forEach((lab) => {
      attachRelatedRecord(lab, "LabtestList", [
        lab.reportDate,
        lab.createdAt,
        lab.created_at,
      ]);
    });

    Object.values(grouped).forEach((group) => {
      group.LabtestList = dedupeLabRecords(group.LabtestList);
    });

    // Patients created without appointments still need a timeline card
    // (Patient Info). Built here, inside the memo, rather than as a mutation
    // of the returned/cached object afterward — mutating a useMemo result in
    // place would leak into the next render whenever deps don't change.
    if (patientInfo && !Array.isArray(patientInfo) && Object.keys(grouped).length === 0) {
      const registrationKey =
        formatDateKey(
          patientInfo.registrationDate ||
            patientInfo.registration_date ||
            patientInfo.createdAt ||
            patientInfo.created_at ||
            patientInfo.creationDate
        ) || dayjs().format("YYYY-MM-DD");
      grouped[registrationKey] = initializeGroup();
      grouped[registrationKey].dateKey = registrationKey;
    }

    return grouped;
  }, [appointmentList, PrescritionList, vitalSigns, LabtestList, patientInfo]);

  const getGroupSortTime = (group) => {
    const appointmentDt = parseTimelineDateTime(
      getAppointmentDateTimeValue(group?.appointments?.[0])
    );
    if (appointmentDt) return appointmentDt.valueOf();
    if (group?.dateKey && group.dateKey !== "unknown-date") {
      const dateOnly = dayjs(group.dateKey);
      if (dateOnly.isValid()) return dateOnly.valueOf();
    }
    return 0;
  };

  // Newest visit first; keep unknown dates at the end
  const groupedDates = Object.keys(groupedData).sort((a, b) => {
    const groupA = groupedData[a];
    const groupB = groupedData[b];
    const unknownA = a === "unknown-date" || groupA?.dateKey === "unknown-date";
    const unknownB = b === "unknown-date" || groupB?.dateKey === "unknown-date";
    if (unknownA && !unknownB) return 1;
    if (!unknownA && unknownB) return -1;
    const timeDiff = getGroupSortTime(groupB) - getGroupSortTime(groupA);
    if (timeDiff !== 0) return timeDiff;
    return String(b).localeCompare(String(a));
  });

  const visitPrescriptionIdsForPrint = (group) => {
    const fromPrescriptions = (group?.prescriptions || [])
      .map((rx) => getPrescriptionRecordId(rx))
      .filter((id) => id != null && id !== "");
    if (fromPrescriptions.length > 0) {
      return [...new Set(fromPrescriptions.map((id) => String(id)))];
    }
    const headerRx = toVisitScopeId(group?.prescriptionId);
    if (!headerRx) return [];
    const ownedBySibling = Object.values(groupedData).some((other) => {
      if (other === group) return false;
      if (other?.dateKey !== group?.dateKey) return false;
      return (other.prescriptions || []).some((rx) =>
        idsEqual(getPrescriptionRecordId(rx), headerRx)
      );
    });
    return ownedBySibling ? [] : [headerRx];
  };

  const handleTabChange = (dateKey, newTabIndex) => {
    setActiveTabs((prev) => ({ ...prev, [dateKey]: newTabIndex }));
  };

  const isPrescriptionCreatedToday = (prescription) => {
    const createdAt = prescription?.createdAt;
    if (!createdAt) return false;
    const createdDate = dayjs(createdAt);
    if (!createdDate.isValid()) return false;
    return createdDate.startOf("day").isSame(dayjs().startOf("day"));
  };

  const normalizeMedicinesForEdit = (medicines) =>
    (Array.isArray(medicines) ? medicines : []).map((med) => ({
      ...med,
      name: med?.name || med?.medicineName || "",
      dosage: med?.dosage || "",
      frequency: med?.frequency || "",
      duration: med?.duration || "",
      notes: med?.notes || med?.note || "",
    }));

  const handleStartEditPrescription = (prescription) => {
    if (!canEditPrescription || !prescription?.id || !isPrescriptionCreatedToday(prescription)) {
      return;
    }
    const medicinesArray = Array.isArray(prescription.medicines)
      ? prescription.medicines
      : Array.isArray(prescription.medicine)
      ? prescription.medicine
      : [];
    setEditingPrescriptionId(prescription.id);
    setEditedMedicines(normalizeMedicinesForEdit(medicinesArray));
  };

  const handleCancelEditPrescription = () => {
    setEditingPrescriptionId(null);
    setEditedMedicines([]);
    setSavingPrescriptionId(null);
  };

  const handleEditMedicineFieldChange = (index, field, value) => {
    setEditedMedicines((prev) =>
      prev.map((medicine, idx) => (idx === index ? { ...medicine, [field]: value } : medicine))
    );
  };

  const handleDurationNumericChange = (index, value) => {
    const numericValue = String(value || "").replace(/\D/g, "");
    handleEditMedicineFieldChange(index, "duration", numericValue);
  };

  const handleOpenSaveConfirmation = (prescription) => {
    if (!canEditPrescription || !prescription?.id || !isPrescriptionCreatedToday(prescription)) {
      return;
    }
    setPendingSavePrescription(prescription);
    setSaveConfirmOpen(true);
  };

  const handleCloseSaveConfirmation = () => {
    setSaveConfirmOpen(false);
    setPendingSavePrescription(null);
  };

  const getDistinctMedicines = (medicines) => {
    const seen = new Set();
    return (Array.isArray(medicines) ? medicines : []).filter((medicine) => {
      const medicineName = (medicine?.name || medicine?.medicineName || "").toLowerCase().trim();
      if (!medicineName || seen.has(medicineName)) return false;
      seen.add(medicineName);
      return true;
    });
  };

  const findMedicineIdByName = (name) => {
    const normalizedName = String(name || "").toLowerCase().trim();
    if (!normalizedName) return null;
    const existing = medicineList.find(
      (med) => String(med.name || med.medicineName || "").toLowerCase().trim() === normalizedName
    );
    return existing?.id || existing?.medicineId || null;
  };

  const ensureEditedMedicineIds = async () => {
    const updatedMedicines = [...editedMedicines];
    for (let i = 0; i < updatedMedicines.length; i += 1) {
      const med = updatedMedicines[i];
      const medicineName = String(med?.name || med?.medicineName || "").trim();
      if (!medicineName) continue;
      if (med?.medicineId || med?.id) continue;

      const existingId = findMedicineIdByName(medicineName);
      if (existingId) {
        updatedMedicines[i] = { ...med, medicineId: existingId };
        continue;
      }

      const createdMedicine = await createNewMedicine({
        medicineName,
        medicineType: "Homeopathy",
        manufacturer: "Unknown",
        price: 0,
        quantityInStock: 0,
        medicineDetails: "tablet",
      });
      const createdId = createdMedicine?.id || null;
      updatedMedicines[i] = { ...med, medicineId: createdId };
    }
    setEditedMedicines(updatedMedicines);
    return updatedMedicines;
  };

  const handleAddMedicineRow = () => {
    setEditedMedicines((prev) => [
      ...prev,
      {
        name: "",
        medicineName: "",
        medicineId: null,
        dosage: "",
        frequency: "",
        duration: "",
        notes: "",
      },
    ]);
  };

  const handleRemoveMedicineRow = (index) => {
    setEditedMedicines((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSavePrescription = async (prescription) => {
    if (!canEditPrescription || !prescription?.id || !isPrescriptionCreatedToday(prescription)) {
      toast.info("Only today's prescriptions can be edited.");
      return;
    }
    try {
      setSavingPrescriptionId(prescription.id);
      const medicinesWithIds = await ensureEditedMedicineIds();
      const updatedPayload = {
        ...prescription,
        medicines: medicinesWithIds.map((medicine) => ({
          ...medicine,
          medicineName: medicine.name || medicine.medicineName || "",
          medicineId: medicine.medicineId || medicine.id || findMedicineIdByName(medicine.name),
          note: medicine.notes || medicine.note || "",
        })),
      };
      await updatePrescription(prescription.id, updatedPayload);
      toast.success("Prescription updated successfully");
      handleCancelEditPrescription();
      await fetchPatientPrescription();
    } catch (error) {
      console.error("[OpdReports] Failed to update prescription:", error);
      toast.error("Failed to update prescription");
    } finally {
      setSavingPrescriptionId(null);
    }
  };

  const getPrescriptionMedicinesForIssue = (prescription) => {
    const medicinesArray = Array.isArray(prescription?.medicines)
      ? prescription.medicines
      : Array.isArray(prescription?.medicine)
      ? prescription.medicine
      : [];
    return medicinesArray.map((medicine) => ({
      ...medicine,
      name: medicine?.name || medicine?.medicineName || "",
    }));
  };

  const handleIssueMedicine = (prescription) => {
    if (!isPharmacyAdmin || !prescription) return;
    setIssueMedicinePrescription(prescription);
    setIssueMedicineOpen(true);
  };

  const handleIssueMedicineClose = () => {
    setIssueMedicineOpen(false);
    setIssueMedicinePrescription(null);
  };

  const handleIssueMedicineSubmit = async () => {
    try {
      await fetchPatientPrescription();
    } catch (error) {
      console.error("[OpdReports] Failed to refresh after issuing medicine:", error);
    }
  };

  // const exportToPDF = async () => {
  //   const input = reportRef.current;
  //   if (!input) return;

  //   const canvas = await html2canvas(input, {
  //     scale: 2, // better quality
  //     useCORS: true,
  //   });
  //   const imgData = canvas.toDataURL("image/png");

  //   const pdf = new jsPDF("p", "mm", "a4");
  //   const pdfWidth = pdf.internal.pageSize.getWidth();
  //   const pdfHeight = pdf.internal.pageSize.getHeight();

  //   const imgWidth = pdfWidth;
  //   const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //   let heightLeft = imgHeight;
  //   let position = 0;

  //   pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  //   heightLeft -= pdfHeight;

  //   while (heightLeft > 0) {
  //     position -= pdfHeight;
  //     pdf.addPage();
  //     pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  //     heightLeft -= pdfHeight;
  //   }

  //   pdf.save(`opd_summary_${new Date().toISOString().slice(0, 10)}.pdf`);
  // };
    
  // Resolve the doctor row (registration no. / department / specialization) for
  // whichever doctor is attached to this day's prescription or appointment.
  const getDoctorDetailsForDayData = async (dayData) => {
    const doctorId =
      (dayData.prescriptions || []).find((rx) => rx?.doctorId)?.doctorId ??
      (dayData.appointments || []).find((a) => a?.doctorId)?.doctorId ??
      null;
    if (!doctorId) return null;
    try {
      const doctors = await getAllDoctors();
      return (doctors || []).find((d) => Number(d.id) === Number(doctorId)) || null;
    } catch (error) {
      console.warn("Failed to load doctor details for prescription print", error);
      return null;
    }
  };

  // Path tests the doctor advised on the prescription (rx.patientLabTests) are
  // a different thing from LabtestList (completed lab REPORTS) that dayData
  // already carries — surface their names too, even when no report has been
  // processed yet. The backend now embeds `testName` directly on each entry,
  // so this normally makes zero extra calls; the getLabtestsList() lookup is
  // kept only as a fallback for any entry that still arrives without one.
  const getAdvisedLabTestsForDayData = async (dayData) => {
    const entries = [];
    const seenIds = new Set();
    (dayData.prescriptions || []).forEach((rx) => {
      (rx.patientLabTests || []).forEach((entry) => {
        const id = entry?.labTestId ?? entry?.testId ?? entry?.id ?? entry;
        if (id == null || id === "" || seenIds.has(id)) return;
        seenIds.add(id);
        entries.push({ id, testName: entry?.testName || entry?.name || "" });
      });
    });
    if (entries.length === 0) return [];

    const missingIds = entries.filter((e) => !e.testName).map((e) => e.id);
    let nameById = new Map();
    if (missingIds.length > 0) {
      try {
        const allLabTests = await getLabtestsList();
        nameById = new Map(
          (allLabTests || []).map((lt) => [
            lt.id,
            lt.testName || lt.name || lt.test_name || lt.labTestName,
          ])
        );
      } catch (error) {
        console.warn("Failed to resolve advised lab tests for prescription print", error);
      }
    }

    const names = entries
      .map((e) => e.testName || nameById.get(e.id))
      .filter(Boolean);

    // Belt-and-braces dedupe by name too, in case the same test comes back
    // under two different catalog ids (or one entry arrived without an id).
    const seenNames = new Set();
    return names
      .filter((name) => {
        const key = name.trim().toLowerCase();
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      })
      .map((testName) => ({ testName }));
  };

  // print summary data — also used to render the on-screen "OPD Summary" tab
  // preview, so it's memoized to avoid rebuilding on every render.
  const buildOpdSummaryHtml = useCallback(async (groupKey) => {
    const dayData = groupedData[groupKey] || {};
    const appointment = dayData.appointments?.[0];
    const appointmentDt = parseTimelineDateTime(
      getAppointmentDateTimeValue(appointment)
    );
    const visitDate = appointmentDt
      ? appointmentDt.format("DD MMM YYYY, hh:mm A")
      : dayData.dateKey && dayData.dateKey !== "unknown-date" && dayjs(dayData.dateKey).isValid()
      ? dayjs(dayData.dateKey).format("DD MMM YYYY")
      : dayjs().format("DD MMM YYYY");

    const [doctorDetails, advisedLabTests] = await Promise.all([
      getDoctorDetailsForDayData(dayData),
      getAdvisedLabTestsForDayData(dayData),
    ]);

    // Investigations here should read as "tests advised on this prescription" —
    // just the names. Completed-report data (results/sub-category breakdowns)
    // has its own dedicated print (Print > Lab Test Reports in the timeline's
    // print menu), so it must not be mixed into this list: a test that's both
    // advised and already reported would otherwise show up twice (once as a
    // bare name, once with its full result text), since the two records don't
    // share an identity key that dedupeLabRecords can match on.
    return buildOpdSummaryDocumentHtml({
      hospitalName,
      hospitalAddress,
      hospitalPhone,
      patient: patientInfo,
      visitDate,
      dayData: {
        ...dayData,
        LabtestList: advisedLabTests,
      },
      doctorDetails,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedData, hospitalName, hospitalAddress, hospitalPhone, patientInfo]);

  const exportToPDF = async (date) => {
    if (!date) return;
    try {
      await exportPatientSummaryDocumentToPdf({
        bodyHtml: await buildOpdSummaryHtml(date),
        filename: getPatientPdfFilename("opd_summary", patientId),
      });
    } catch (error) {
      console.error("Failed to export OPD summary PDF", error);
    }
  };

  const handlePrint = async (date) => {
    if (!date) return;
    printPatientSummaryDocument({
      title: "OPD Summary",
      bodyHtml: await buildOpdSummaryHtml(date),
    });
  };

  return (
    <Box sx={{ p: showHeader ? 4 : 2, backgroundColor: "#fff", minHeight: showHeader ? "100vh" : "auto" }}>
      {showHeader && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Header
            title={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={() => onBack ? onBack() : navigate(-1)}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    mr: 1,
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  {
                    patientInfo?.name
                      ?.split(" ")
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(" ")
                  } Timeline
                </Typography>
              </Box>
            }
          />
        </Box>
      )}

      <Box sx={{ position: "relative", ml: 6 }}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: -49,
            bottom: 0,
            width: "2px",
            backgroundColor: primaryColor,
          }}
        />
        {groupedDates.map((date) => {
          const activeTab = activeTabs[date] ?? defaultTimelineTabIndex;
          const visitGroup = groupedData[date] || {};
          const { appointments } = visitGroup;
          const visitAppointment = appointments?.[0];
          const visitDateTime =
            formatTimelineDateTime(getAppointmentDateTimeValue(visitAppointment)) ||
            (visitGroup.dateKey &&
            visitGroup.dateKey !== "unknown-date" &&
            dayjs(visitGroup.dateKey).isValid()
              ? dayjs(visitGroup.dateKey).format("DD MMM YYYY")
              : "");
          const visitAppointmentId =
            visitGroup.appointmentId ?? getAppointmentId(visitAppointment);
          const visitPrescriptionId =
            visitGroup.prescriptionId ??
            getLinkedPrescriptionId(visitAppointment) ??
            getPrescriptionRecordId(visitGroup.prescriptions?.[0]);
          const visitAppointmentStatus = visitAppointment
            ? getAppointmentStatusUpper(visitAppointment)
            : "";
          const visitAppointmentStatusColor =
            visitAppointmentStatus === "CANCELLED"
              ? "error.main"
              : visitAppointmentStatus === "COMPLETED"
              ? "success.main"
              : "text.secondary";
          const visitIsCancelled = visitAppointmentStatus === "CANCELLED";
          const headerDateLabel =
            visitDateTime ||
            (date === "unknown-date" || visitGroup.dateKey === "unknown-date"
              ? "Appointments (Date Unknown)"
              : date);

          return (
            <Box key={date} sx={{ display: "flex", mb: 2.5, position: "relative" }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: primaryColor,
                  position: "absolute",
                  left: "-54px",
                  top: "16px",
                }}
              />

              <Box sx={{ flex: 2 }}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: primaryColor }}
                      >
                        {headerDateLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Appointment ID: {displayId(visitAppointmentId)}
                        {visitAppointmentStatus && (
                          <Box
                            component="span"
                            sx={{ color: visitAppointmentStatusColor, fontWeight: 700 }}
                          >
                            {" "}({visitAppointmentStatus})
                          </Box>
                        )}
                        {"  |  "}
                        Prescription ID: {displayId(visitPrescriptionId)}
                      </Typography>
                    </Box>
                    <PatientTimelinePrintMenu
                      patientId={patientId}
                      patient={patientInfo}
                      dateKey={
                        visitGroup.dateKey && visitGroup.dateKey !== "unknown-date"
                          ? visitGroup.dateKey
                          : null
                      }
                      appointmentId={visitAppointmentId}
                      prescriptionId={visitPrescriptionId}
                      prescriptionIds={visitPrescriptionIdsForPrint(visitGroup)}
                      appointmentDateTime={getAppointmentDateTimeValue(visitAppointment)}
                      siblingAppointments={Object.values(groupedData)
                        .filter((group) => group.dateKey && group.dateKey === visitGroup.dateKey)
                        .map((group) => ({
                          id:
                            group.appointmentId ??
                            getAppointmentId(group.appointments?.[0]),
                          dateTime: getAppointmentDateTimeValue(group.appointments?.[0]),
                          prescriptionIds: visitPrescriptionIdsForPrint(group),
                        }))
                        .filter((visit) => visit.id != null && visit.id !== "")}
                      onPrintSummary={() => handlePrint(date)}
                      onExportPdf={() => exportToPDF(date)}
                      labReports={visitGroup.LabtestList || []}
                      invoiceTypes={isPharmacyAdmin ? ["MEDICINE"] : null}
                      showLabReports={!isPharmacyAdmin}
                      doctorName={
                        visitGroup.appointments?.find((a) =>
                          String(a?.doctorName || "").trim()
                        )?.doctorName ||
                        visitGroup.prescriptions?.find((p) =>
                          String(p?.doctorName || "").trim()
                        )?.doctorName ||
                        null
                      }
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Tabs
                    value={activeTab}
                    onChange={(e, newTab) => handleTabChange(date, newTab)}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ mb: 2 }}
                  >
                    {tabLabels.map((label, index) => (
                      <Tab label={label} key={index} />
                    ))}
                  </Tabs>
                  <Divider sx={{ mb: 2 }} />

                  {activeTab === 0 && (
                    initialDataLoading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : groupedData[date]?.prescriptions?.length === 0 ? (
                      <Typography sx={{ p: 2 }}>
                        {visitIsCancelled
                          ? "No prescriptions available as appointment was cancelled"
                          : "No prescriptions available"}
                      </Typography>
                    ) : (
                      groupedData[date].prescriptions.map((prescription, index) => {
                        // Handle medicines array - check both 'medicines' and 'medicine' fields
                        const medicinesArray = Array.isArray(prescription.medicines) 
                          ? prescription.medicines 
                          : (Array.isArray(prescription.medicine) ? prescription.medicine : []);
                        
                        console.log(`[OpdReports] Rendering prescription ${prescription.id}:`, {
                          medicinesCount: medicinesArray.length,
                          medicines: medicinesArray,
                          prescriptionKeys: Object.keys(prescription),
                          hasMedicines: !!prescription.medicines,
                          hasMedicine: !!prescription.medicine,
                          medicinesType: typeof prescription.medicines,
                          medicinesValue: prescription.medicines
                        });
                        
                        return (
                          <Box key={prescription.id || index} sx={{ mb: 2 }}>
                            {(canEditPrescription && medicinesArray.length > 0 && isPrescriptionCreatedToday(prescription)) ||
                            (isPharmacyAdmin && medicinesArray.length > 0) ? (
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
                                {canEditPrescription && medicinesArray.length > 0 && isPrescriptionCreatedToday(prescription) && (
                                  editingPrescriptionId === prescription.id ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleAddMedicineRow}
                                        disabled={savingPrescriptionId === prescription.id}
                                      >
                                        Add Medicine
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleCancelEditPrescription}
                                        disabled={savingPrescriptionId === prescription.id}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleOpenSaveConfirmation(prescription)}
                                        disabled={savingPrescriptionId === prescription.id}
                                      >
                                        {savingPrescriptionId === prescription.id ? "Saving..." : "Save"}
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleStartEditPrescription(prescription)}
                                    >
                                      Edit Prescription
                                    </Button>
                                  )
                                )}
                                {isPharmacyAdmin && medicinesArray.length > 0 && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleIssueMedicine(prescription)}
                                  >
                                    Issue Medicine
                                  </Button>
                                )}
                              </Box>
                            ) : null}

                            {medicinesArray.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: primaryColor }}>
                                    <TableCell sx={{ color: "#fff" }}>Medicine Name</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>Dosage</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>Frequency</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>Duration</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>Notes</TableCell>
                                    {editingPrescriptionId === prescription.id && (
                                      <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(editingPrescriptionId === prescription.id ? editedMedicines : medicinesArray).map(
                                    (med, i) => (
                                      <TableRow key={i}>
                                        <TableCell>
                                          {editingPrescriptionId === prescription.id ? (
                                            <Autocomplete
                                              freeSolo
                                              fullWidth
                                              options={getDistinctMedicines(medicineList).map(
                                                (option) => option.name || option.medicineName || ""
                                              )}
                                              value={med.name || med.medicineName || ""}
                                              onChange={(_, newValue) => {
                                                const selectedName = String(newValue || "");
                                                handleEditMedicineFieldChange(i, "name", selectedName);
                                                handleEditMedicineFieldChange(
                                                  i,
                                                  "medicineId",
                                                  findMedicineIdByName(selectedName)
                                                );
                                              }}
                                              onInputChange={(_, newInputValue) => {
                                                handleEditMedicineFieldChange(i, "name", newInputValue);
                                                handleEditMedicineFieldChange(
                                                  i,
                                                  "medicineId",
                                                  findMedicineIdByName(newInputValue)
                                                );
                                              }}
                                              renderInput={(params) => (
                                                <TextField {...params} fullWidth size="small" label="Medicine" />
                                              )}
                                            />
                                          ) : (
                                            med.name || med.medicineName || "N/A"
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {editingPrescriptionId === prescription.id ? (
                                            <TextField
                                              fullWidth
                                              select
                                              size="small"
                                              value={med.dosage || ""}
                                              onChange={(e) =>
                                                handleEditMedicineFieldChange(i, "dosage", e.target.value)
                                              }
                                            >
                                              {dosageOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                  {option}
                                                </MenuItem>
                                              ))}
                                            </TextField>
                                          ) : (
                                            med.dosage || "N/A"
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {editingPrescriptionId === prescription.id ? (
                                            <TextField
                                              fullWidth
                                              select
                                              size="small"
                                              value={med.frequency || ""}
                                              onChange={(e) =>
                                                handleEditMedicineFieldChange(i, "frequency", e.target.value)
                                              }
                                            >
                                              {frequencyOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                  {option}
                                                </MenuItem>
                                              ))}
                                            </TextField>
                                          ) : (
                                            med.frequency || "N/A"
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {editingPrescriptionId === prescription.id ? (
                                            <TextField
                                              fullWidth
                                              size="small"
                                              value={med.duration || ""}
                                              onChange={(e) => handleDurationNumericChange(i, e.target.value)}
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">days</InputAdornment>
                                                ),
                                              }}
                                            />
                                          ) : (
                                            med.duration || "N/A"
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {editingPrescriptionId === prescription.id ? (
                                            <TextField
                                              fullWidth
                                              size="small"
                                              value={med.notes || med.note || ""}
                                              onChange={(e) =>
                                                handleEditMedicineFieldChange(i, "notes", e.target.value)
                                              }
                                            />
                                          ) : (
                                            med.notes || med.note || ""
                                          )}
                                        </TableCell>
                                        {editingPrescriptionId === prescription.id && (
                                          <TableCell>
                                            <IconButton
                                              color="error"
                                              onClick={() => handleRemoveMedicineRow(i)}
                                              disabled={editedMedicines.length <= 1}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2" sx={{ p: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                No medicines in this prescription
                              </Typography>
                            )}
                          </Box>
                        );
                      })
                    )
                  )}

                  {activeTab === 1 && (
                    initialDataLoading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (groupedData[date]?.vitalSigns.length === 0) ? (
                      <Typography sx={{ p: 2 }}>No vital signs available</Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: primaryColor }}>
                            <TableCell sx={{ color: "#fff" }}>BP</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Weight</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Height</TableCell>
                            <TableCell sx={{ color: "#fff" }}>O₂</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Pulse</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Respiration</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Recorded At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedData[date].vitalSigns.map((vs, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{vs.bpReading}</TableCell>
                              <TableCell>{vs.weightReading}</TableCell>
                              <TableCell>{vs.heightReading}</TableCell>
                              <TableCell>{vs.oxygenSaturation}</TableCell>
                              <TableCell>{vs.pulseRate}</TableCell>
                              <TableCell>{vs.respirationRate}</TableCell>
                              <TableCell>{formatDateKey(vs.recordedAt)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )
                  )}

                  {activeTab === 2 && (
                    initialDataLoading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (groupedData[date]?.LabtestList?.length || 0) === 0 ? (
                      <Typography sx={{ p: 2 }}>
                        {visitIsCancelled
                          ? "No lab tests available as appointment was cancelled"
                          : "No lab tests available"}
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: primaryColor }}>
                            <TableCell sx={{ color: "#fff" }}>Report ID</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Test Name</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Result</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Ref. Value</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Report Date</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedData[date].LabtestList.map((report, idx) => {
                            const { resultLines, referenceLines, remarks } =
                              buildResultReferenceLines(report);
                            return (
                              <TableRow key={report.reportId || `${report.testName}-${idx}`}>
                                <TableCell>{displayId(report.reportId)}</TableCell>
                                <TableCell>{report.testName}</TableCell>
                                <TableCell>
                                  {resultLines.map((line, i) => (
                                    <div key={i}>{line}</div>
                                  ))}
                                  {remarks ? (
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      Remarks: {remarks}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell>
                                  {referenceLines.map((line, i) => (
                                    <div key={i}>{line}</div>
                                  ))}
                                </TableCell>
                                <TableCell>{formatReportDateOnly(report.reportDate)}</TableCell>
                                <TableCell>{report.resultStatus || "—"}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )
                  )}

                  {activeTab === 3 && (
                    <Box p={3}>
                      <Paper elevation={0} sx={{ padding: { xs: 1, sm: 2 }, backgroundColor: "#f5f7fa" }} ref={reportRef}>
                        <OpdSummaryPreview date={date} buildHtml={buildOpdSummaryHtml} />
                      </Paper>
                    </Box>
                  )}
                </Paper>
              </Box>

            </Box>
          );
        })}
      </Box>
      <Dialog open={saveConfirmOpen} onClose={handleCloseSaveConfirmation} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Prescription Update</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to save these prescription changes?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveConfirmation} disabled={!!savingPrescriptionId}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!pendingSavePrescription) return;
              const targetPrescription = pendingSavePrescription;
              handleCloseSaveConfirmation();
              await handleSavePrescription(targetPrescription);
            }}
            disabled={!!savingPrescriptionId || !pendingSavePrescription}
          >
            Confirm Save
          </Button>
        </DialogActions>
      </Dialog>
      {isPharmacyAdmin && (
        <IssueMedicineModal
          open={issueMedicineOpen}
          onClose={handleIssueMedicineClose}
          medicines={getPrescriptionMedicinesForIssue(issueMedicinePrescription)}
          onSubmit={handleIssueMedicineSubmit}
          patientId={patientInfo?.id ?? patientId}
          doctorName={issueMedicinePrescription?.doctorName || null}
          doctorId={
            issueMedicinePrescription?.doctorId ||
            issueMedicinePrescription?.doctor_id ||
            null
          }
          appointmentId={
            issueMedicinePrescription?.appointmentId ||
            issueMedicinePrescription?.appointment_id ||
            getLinkedAppointmentId(issueMedicinePrescription) ||
            null
          }
          prescriptionId={
            issueMedicinePrescription?.id ||
            issueMedicinePrescription?.prescriptionId ||
            issueMedicinePrescription?.prescription_id ||
            null
          }
          departmentId={
            issueMedicinePrescription?.departmentId ||
            issueMedicinePrescription?.department_id ||
            null
          }
        />
      )}
    </Box>
  ); 
};

// Renders the on-screen "OPD Summary" tab using the same HTML the Print/Export
// buttons produce (buildOpdSummaryDocumentHtml), so the tab matches the
// prescription print design instead of a separate plain-table layout.
const OpdSummaryPreview = ({ date, buildHtml }) => {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    buildHtml(date)
      .then((result) => {
        if (!cancelled) setHtml(result);
      })
      .catch((error) => {
        console.error("Failed to build OPD summary preview", error);
        if (!cancelled) setHtml("");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, buildHtml]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 1,
        boxShadow: 1,
        overflow: "auto",
        "& .opd-page": { maxWidth: "100%" },
      }}
    >
      <style>{buildPatientDocumentStyles()}</style>
      <div dangerouslySetInnerHTML={{ __html: html || "" }} />
    </Box>
  );
};

export default PatientTimeline;